import type { Field, Form } from 'formik'
import type { Dispatch, ReactElement, SetStateAction } from 'react'
import { useMemo, useState } from 'react'
import type { GroupBase, MultiValueProps } from 'react-select'
import { components } from 'react-select'
import { AsyncPaginate } from 'react-select-async-paginate'
import type {
  ComponentProps,
  UseAsyncPaginateParams,
} from 'react-select-async-paginate'

import reactSelectStyles from './filterConditionStyles'
import type { DropdownOption, NegativeInfoDropdownOption } from '../types'

type customType = {
  selectProps: {
    separator?: any
    setSeparator?: any
    canBeNegative?: boolean
    setValues: Dispatch<SetStateAction<any>>
  }
}

const CustomMultiValue = (props: MultiValueProps<any> & customType) => {
  const isNegative = props.getValue()[props.index]?.isNegative

  const onButtonClick = (e: any) => {
    e.stopPropagation()

    props?.selectProps.setValues((values: any[]) => {
      const list = [...values]
      if (list[props.index].isNegative) {
        list[props.index].isNegative = false
      } else {
        list[props.index].isNegative = true
      }
      return list
    })
  }

  if (props.index === 0) {
    return (
      <components.MultiValue {...props}>
        {props.selectProps.canBeNegative ? (
          <button
            type="button"
            onClick={onButtonClick}
            className={`${isNegative ? 'line-through' : ''}`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {props.children}
          </button>
        ) : (
          props.children
        )}
      </components.MultiValue>
    )
  } else {
    return (
      <div className="inline-flex">
        {props.selectProps.separator != null && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              props?.selectProps?.setSeparator?.(
                props.selectProps.separator === 'and' ? 'or' : 'and',
              )
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="bg-gray-300 rounded-lg px-1 my-0.5 hover:bg-gray-400"
          >
            {props?.selectProps.separator}
          </button>
        )}
        <components.MultiValue {...props}>
          {props.selectProps.canBeNegative ? (
            <button
              type="button"
              onClick={onButtonClick}
              className={`${isNegative ? 'line-through' : ''}`}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {props.children}
            </button>
          ) : (
            props.children
          )}
        </components.MultiValue>
      </div>
    )
  }
}

type AsyncPaginateProps<
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean,
> = UseAsyncPaginateParams<OptionType, Group, Additional> &
  ComponentProps<OptionType, Group, IsMulti> & {
    className?: string
    onChange: { (opt: NegativeInfoDropdownOption[] | null): void }
    defaultValue: DropdownOption
    isSearchable: boolean
    styles: any
    separator?: any
    setSeparator?: any
    components?: any
    value?: DropdownOption[]
    isClearable?: boolean
    isMulti?: boolean
    canBeNegative?: boolean
    setValues: Dispatch<SetStateAction<any>>
    onKeyDown?: (e: any) => void
    menuPortalTarget?: HTMLElement | null
    menuPosition?: 'fixed' | 'absolute'
  }

type AsyncPaginateType = <
  OptionType,
  Group extends GroupBase<OptionType>,
  Additional,
  IsMulti extends boolean = false,
>(
  props: AsyncPaginateProps<OptionType, Group, Additional, IsMulti>,
) => ReactElement

const CustomAsyncPaginate = AsyncPaginate as AsyncPaginateType

export default function SeparatableAsyncPaginateDropdown({
  fetcher,
  onChange,
  defaultValue,
  form,
  field,
  customComponent,
  onSubmit,
  separator,
  setSeparator,
  isClearable,
  isMulti,
  value,
  canBeNegative = false,
  isReady = false,
  customStyles,
  ...rest
}: {
  fetcher: any // TODO: fix type issue and change type to: { (args: PaginatedQueryParams): Promise<PaginatedResponse<any>> }
  onChange: any
  form?: typeof Form
  field?: typeof Field
  defaultValue: any
  customComponent?: boolean
  separator?: any
  setSeparator?: any
  isClearable?: boolean
  isMulti?: boolean
  canBeNegative?: boolean
  isReady: boolean
  value?: DropdownOption[]
  onSubmit?: () => void
  customStyles?: any
}) {
  const [values, setValues] = useState(defaultValue ? defaultValue : [])

  useMemo(() => {
    if (!defaultValue || defaultValue.length === 0) {
      setValues([])
    } else {
      setValues(defaultValue)
    }
  }, [defaultValue])

  useMemo(() => {
    if (isReady && values) {
      onChange?.(values)
    }
  }, [values])

  const loadOptions: any = async (
    search: string,
    loadedOptions: any,
    { page, hasMore }: { page: number; hasMore: boolean },
  ) => {
    try {
      const normalizedSearch = search.replace(/—/g, '-')
      const { data, meta } = await fetcher({
        page,
        limit: 10,
        search: normalizedSearch,
      })
      return {
        options: data,
        hasMore: meta.page < meta.totalPages,
        additional: {
          page: meta.page + 1,
        },
      }
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      }
    }
  }

  return (
    <CustomAsyncPaginate
      className="w-full text-sm font-semibold"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      loadOptions={loadOptions}
      onChange={(opt: NegativeInfoDropdownOption[] | null) => {
        const valueList = opt?.map((item) => {
          const found = values.find(
            (i: NegativeInfoDropdownOption) => i.value === item.value,
          )
          if (found) {
            item.isNegative = found.isNegative
          } else {
            item.isNegative = false
          }
          return item
        })
        if (valueList) {
          if (valueList?.length > 1 && !separator) {
            setSeparator?.('and')
          }
          if (valueList?.length <= 1 && separator) {
            setSeparator?.(undefined)
          }
        } else {
          if (separator) {
            setSeparator?.(undefined)
          }
        }
        setValues(valueList)
      }}
      defaultValue={defaultValue}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onSubmit) {
          onSubmit()
        }
      }}
      value={values}
      isSearchable={true}
      styles={customStyles ?? reactSelectStyles}
      additional={{
        page: 1,
        hasMore: true,
      }}
      separator={separator}
      setSeparator={setSeparator}
      components={{
        MultiValue: CustomMultiValue,
      }}
      canBeNegative={canBeNegative}
      isClearable={true}
      isMulti={isMulti}
      setValues={setValues}
      {...rest}
    />
  )
}
